import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconEndAdornment } from 'components/Button';
import { OrderedList, UnorderedList } from 'components/Lists';
import { Heading } from 'components/Typography';
import { LearningPathCtx, withLearningPathContext } from 'domains/learning-path/context';
import { LearningPathStepID } from 'domains/learning-path/steps';
import { MarkdownText } from 'domains/contentful/MarkdownText';

const HowToUseItContent: React.FC = () => {
  const { t } = useTranslation();

  const {
    learningPath: {
      howToUseItSection: {
        fields: { howToUseIt, examples, directions, heresATip },
      },
    },
    onNavigateToStep,
  } = useContext(LearningPathCtx);

  const handleClick = () => onNavigateToStep(LearningPathStepID.helpfulTips, true);

  return (
    <>
      <Heading level="h3" useDefaultMargins>
        {t('learningPath.howToUseIt')}
      </Heading>
      <MarkdownText useDefaultMargins>{howToUseIt}</MarkdownText>

      {directions && (
        <OrderedList className="mb-40">
          {directions.map(({ fields: { directionTitle, directionDescription }, sys: { id } }, index) => (
            <OrderedList.Item key={id} number={index + 1}>
              <Heading level="h5" className="mb-8 font-semibold">
                {directionTitle}
              </Heading>
              {directionDescription && <MarkdownText>{directionDescription}</MarkdownText>}
            </OrderedList.Item>
          ))}
        </OrderedList>
      )}

      {examples && (
        <>
          <Heading level="h4" useDefaultMargins>
            {t('learningPath.howToUseIt.examples')}
          </Heading>

          <UnorderedList className="mb-40">
            {examples.map(({ fields: { example, description } }) => (
              <UnorderedList.Item key={example}>
                <Heading level="h5" className="mb-8 font-semibold">
                  {example}
                </Heading>

                {description && <MarkdownText>{description}</MarkdownText>}
              </UnorderedList.Item>
            ))}
          </UnorderedList>
        </>
      )}

      {heresATip && (
        <div className="flex flex-col bg-yellow-300 p-40 sm-d:p-32 mb-40 rounded-lg">
          <Heading level="h6" useDefaultMargins>
            {t('learningPath.howToUseIt.tip')}
          </Heading>
          <Heading level="h4" className="whitespace-pre-line">
            {heresATip}
          </Heading>
        </div>
      )}

      <Button
        size="lg"
        onClick={handleClick}
        endAdornment={<IconEndAdornment name="AngleRight" />}
        className="sm-d:w-full"
      >
        {t('learningPath.continueButton')}
      </Button>
    </>
  );
};

export default withLearningPathContext(HowToUseItContent, LearningPathStepID.howToUseIt);
